/**
 * Inventory Service
 * Centralized service for inventory item operations (save, delete, duplicate)
 * Eliminates duplicate logic between mobile and desktop code paths
 */

import { API_ENDPOINTS } from '../config';
import { EditableItem } from '../components/itemEdit';

// ============================================================================
// Types
// ============================================================================

export interface SaveItemOptions {
  token: string;
  item: EditableItem;
  isNewItem: boolean;
  pendingImageId?: number;
  duplicateCollectionId?: number;
  checkResponseForLimit?: (response: Response) => Promise<boolean>;
}

export interface DuplicateInfo {
  duplicate_item_id: number;
  duplicate_item_name: string;
  duplicate_item_quantity: number;
  duplicate_collection_ids: number[];
  /** The new item data the user was trying to save (for Replace option) */
  newItemData?: {
    name: string;
    description?: string;
    quantity?: number;
    color?: string;
    category_ids?: number[];
    image_id?: number;
  };
}

export interface SaveItemResult {
  success: boolean;
  savedItems?: any[];
  newItemId?: number;
  error?: string;
  duplicateInfo?: DuplicateInfo;
}

// DeleteItemOptions and DeleteItemResult removed - no longer used

// ============================================================================
// Save Item
// ============================================================================

/**
 * Save an item (create new or update existing)
 * Handles: validation, API call, duplicate collection assignment, error handling
 * 
 * @param options - Save options including item data, token, and callbacks
 * @returns SaveItemResult with success status and any errors
 */
export async function saveItem(options: SaveItemOptions): Promise<SaveItemResult> {
  const {
    token,
    item,
    isNewItem,
    pendingImageId,
    duplicateCollectionId,
    checkResponseForLimit
  } = options;

  try {
    if (isNewItem) {
      // Create new item
      const resp = await fetch(API_ENDPOINTS.SAVE_ITEMS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            name: item.name,
            description: item.description,
            category_ids: item.categories?.map(c => c.id) || [],
            color: item.color,
            area: item.area,
            location: item.location,
            sub_location: (item as any).sub_location,
            quantity: item.quantity,
            quantity_min: item.quantity_min ?? 1,
            purchase_price: (item as any).purchase_price,
            purchased_at: (item as any).purchased_at,
            condition: item.condition || null,
            is_grocery: item.is_grocery,
            uit_id: item.uit_id,
            unit: item.unit,
            is_checked: item.is_checked,
            space_id: item.space_id,
          }],
          image_id: pendingImageId,
        }),
      });

      if (!resp.ok) {
        // Check for item-limit error
        if (checkResponseForLimit && await checkResponseForLimit(resp.clone())) {
          return { success: false, error: 'Item limit reached' };
        }

        const errorData = await resp.json().catch(() => ({}));
        
        if (resp.status === 409 && errorData.duplicate_item_id) {
          return {
            success: false,
            error: errorData.error || 'An item with this name already exists',
            duplicateInfo: {
              duplicate_item_id: errorData.duplicate_item_id,
              duplicate_item_name: errorData.duplicate_item_name,
              duplicate_item_quantity: errorData.duplicate_item_quantity ?? 1,
              duplicate_collection_ids: errorData.duplicate_collection_ids ?? [],
              newItemData: {
                name: item.name,
                description: item.description,
                quantity: item.quantity,
                color: item.color,
                category_ids: item.categories?.map(c => c.id) || [],
                image_id: pendingImageId,
              },
            },
          };
        }
        
        throw new Error(errorData.error || 'Save failed');
      }

      // If this is a duplicate from a specific collection, add it to that collection
      if (duplicateCollectionId) {
        console.log('[DUPLICATE] Adding new item to collection:', duplicateCollectionId);
        try {
          const saveData = await resp.json();
          const newItemId = saveData.saved_items?.[0]?.id;
          console.log('[DUPLICATE] New item ID:', newItemId);
          
          if (newItemId) {
            // Add the new item to the collection
            const addResp = await fetch(API_ENDPOINTS.ADD_ITEMS_TO_COLLECTION(duplicateCollectionId), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ item_ids: [newItemId] }),
            });
            
            console.log('[DUPLICATE] Add to collection response:', addResp.status, addResp.ok);
            
            if (!addResp.ok) {
              const errorText = await addResp.text();
              console.error('[DUPLICATE] Failed to add to collection:', errorText);
            }
            
            return {
              success: true,
              savedItems: saveData.saved_items,
              newItemId
            };
          }
        } catch (e) {
          console.error('[DUPLICATE] Failed to add duplicate to collection:', e);
          // Don't fail the whole save if collection assignment fails
        }
      }

      const saveData = await resp.json();
      return {
        success: true,
        savedItems: saveData.saved_items,
        newItemId: saveData.saved_items?.[0]?.id
      };

    } else {
      // Update existing item
      const resp = await fetch(API_ENDPOINTS.UPDATE_ITEMS, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: item.id,
            name: item.name,
            description: item.description,
            category_ids: item.categories?.map(c => c.id) || [],
            color: item.color,
            area: item.area,
            location: item.location,
            sub_location: (item as any).sub_location,
            quantity: item.quantity,
            quantity_min: item.quantity_min ?? 1,
            sale_price: (item as any).sale_price,
            purchase_price: (item as any).purchase_price,
            purchased_at: (item as any).purchased_at,
            condition: item.condition || null,
            image_id: item.image_id, // CRITICAL: Include image_id to prevent it from being set to NULL
            is_grocery: item.is_grocery,
            uit_id: item.uit_id,
            unit: item.unit,
            is_checked: item.is_checked,
          }]
        }),
      });

      if (!resp.ok) {
        // Check for item-limit error
        if (checkResponseForLimit && await checkResponseForLimit(resp.clone())) {
          return { success: false, error: 'Item limit reached' };
        }

        const errorData = await resp.json().catch(() => ({}));
        
        if (resp.status === 409) {
          throw new Error(errorData.error || 'An item with this name already exists');
        }
        
        if (resp.status === 403) {
          throw new Error('Permission denied: You do not have write access to this item. It may be a read-only shared item.');
        }
        
        throw new Error(errorData.error || 'Save failed');
      }

      return { success: true };
    }
  } catch (error: any) {
    console.error('[inventoryService] Save error:', error);
    return {
      success: false,
      error: error.message || 'Failed to save item'
    };
  }
}

// NOTE: Delete, add, and remove functions removed - handled by batch-update endpoint
// and delete modal in InventoryView. If needed in future, check git history.

// ============================================================================
// Check for Duplicates
// ============================================================================

export interface DuplicateItem {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  area?: string;
  location?: string;
  image_id?: number;
  image_url?: string;
  thumbnail_url?: string;
  categories?: any[];
}

export interface CheckDuplicateResult {
  has_duplicate: boolean;
  duplicates: DuplicateItem[];
  match_type?: 'exact_name' | 'exact_name_and_image';
  error?: string;
}

/**
 * Check if an item with similar name/image already exists
 * 
 * @param token - Auth token
 * @param name - Item name to check
 * @param spaceId - Active hierarchical space ID
 * @param imageId - Optional image ID to check for exact matches
 * @param excludeId - Optional item ID to exclude (for updates)
 * @returns Duplicate check result with list of matching items
 */
export async function checkDuplicateItem(
  token: string,
  name: string,
  spaceId: number,
  imageId?: number,
  excludeId?: number
): Promise<CheckDuplicateResult> {
  try {
    const response = await fetch(`${API_ENDPOINTS.GET_ITEMS}/check-duplicate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        name: name.trim(),
        space_id: spaceId,
        image_id: imageId,
        exclude_id: excludeId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to check for duplicates');
    }

    const data = await response.json();
    return {
      has_duplicate: data.has_duplicate,
      duplicates: data.duplicates || [],
      match_type: data.match_type
    };
  } catch (error: any) {
    console.error('[inventoryService] Check duplicate error:', error);
    return {
      has_duplicate: false,
      duplicates: [],
      error: error.message || 'Failed to check for duplicates'
    };
  }
}
